/* eslint-disable no-unused-vars */
import { useState, useContext, useEffect } from "react";
import { AdminContext } from "../../../context/AdminContext";
import {
  BarChart3,
  Eye,
  FileText,
  Bookmark,
  TrendingUp,
  Users,
  Clock,
  Star,
} from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";
import { KPICard } from "../../../components/dashboard";

const BlogAnalytics = () => {
  const { fetchBlogAnalytics, blogAnalytics } = useContext(AdminContext);
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setLoading(true);
    await fetchBlogAnalytics();
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const overview =
    blogAnalytics?.analytics?.overview || blogAnalytics?.overview || {};
  const categoryStats =
    blogAnalytics?.analytics?.categoryStats ||
    blogAnalytics?.categoryStats ||
    [];
  const topBlogs =
    blogAnalytics?.analytics?.topBlogs || blogAnalytics?.topBlogs || [];

  const statCards = [
    {
      label: "Total Blogs",
      value: overview.total || overview.totalBlogs || 0,
      icon: FileText,
      color: "text-blue-500",
    },
    {
      label: "Published",
      value: overview.published || overview.publishedBlogs || 0,
      icon: Eye,
      color: "text-green-500",
    },
    {
      label: "Drafts",
      value: overview.draft || overview.draftBlogs || 0,
      icon: Clock,
      color: "text-yellow-500",
    },
    {
      label: "Scheduled",
      value: overview.scheduled || overview.scheduledBlogs || 0,
      icon: Star,
      color: "text-purple-500",
    },
    {
      label: "Total Views",
      value: (overview.totalViews || 0).toLocaleString(),
      icon: TrendingUp,
      color: "text-pink-500",
    },
    {
      label: "Total Bookmarks",
      value: overview.totalBookmarks || 0,
      icon: Bookmark,
      color: "text-indigo-500",
    },
    {
      label: "Engagement Rate",
      value: `${overview.engagementRate || 0}%`,
      icon: Users,
      color: "text-teal-500",
    },
  ];

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-text dark:text-dark-text flex items-center gap-3">
          <BarChart3 className="text-primary" /> Blog Analytics
        </h1>
        <p className="text-gray-500 dark:text-dark-muted mt-1">
          Insights on blog performance, engagement, and content popularity.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
        {statCards.map((stat) => (
          <KPICard
            key={stat.label}
            title={stat.label}
            count={stat.value}
            change={0}
            isPositive={true}
            icon={stat.icon}
            color={stat.color}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Categories */}
        <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border p-6">
          <h3 className="font-bold text-text dark:text-dark-text mb-4 text-lg">
            Top Categories by Views
          </h3>
          {categoryStats.length === 0 ? (
            <p className="text-gray-400 text-sm py-8 text-center">
              No data available yet
            </p>
          ) : (
            <div className="space-y-3">
              {categoryStats.map((stat, idx) => {
                const maxViews = categoryStats[0]?.totalViews || 1;
                const percentage = Math.round(
                  (stat.totalViews / maxViews) * 100,
                );
                return (
                  <div key={stat.id || idx}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-text dark:text-dark-text">
                        {stat.category?.nameEn ||
                          stat.category?.name?.en ||
                          "Unknown"}
                      </span>
                      <span className="text-xs text-gray-400">
                        {stat.totalViews} views · {stat.postCount} posts
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-2.5">
                      <div
                        className="bg-primary rounded-full h-2.5 transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top Blogs */}
        <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border p-6">
          <h3 className="font-bold text-text dark:text-dark-text mb-4 text-lg">
            Top Blogs by Views
          </h3>
          {topBlogs.length === 0 ? (
            <p className="text-gray-400 text-sm py-8 text-center">
              No published blogs yet
            </p>
          ) : (
            <div className="space-y-3">
              {topBlogs.map((blog, idx) => (
                <div
                  key={blog.id}
                  className="flex items-center gap-3 p-3 bg-accent dark:bg-gray-800/50 rounded-xl"
                >
                  <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center flex-shrink-0">
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-text dark:text-dark-text truncate">
                      {blog.titleEn || blog.title?.en || "Untitled"}
                    </p>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-xs text-gray-400">
                        {blog.viewCount?.toLocaleString()}
                      </span>
                      <span className="text-xs text-gray-400">
                        {blog.bookmarkCount || 0}
                      </span>
                      <span className="text-xs text-gray-400">
                        {blog.readingTime} min
                      </span>
                    </div>
                  </div>
                  {blog.category && (
                    <span
                      className="px-2 py-0.5 rounded text-[10px] font-bold flex-shrink-0"
                      style={{
                        backgroundColor: `${blog.category.color}15`,
                        color: blog.category.color,
                      }}
                    >
                      {blog.category.name?.en}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogAnalytics;
